import { useQuery } from "@tanstack/react-query";
import { db } from "@/lib/firebase";
import { collection, getDocs } from "firebase/firestore";

export type CmsSection = {
  key: string;
  title: string;
  content: string;
  metadata: string | null;
};

export function useCms() {
  const { data = [], isLoading } = useQuery<CmsSection[]>({
    queryKey: ["cmsSections"],
    queryFn: async () => {
      const snap = await getDocs(collection(db, "cms_sections"));
      return snap.docs.map(d => {
        const item = d.data();
        return {
          key: item.key || d.id,
          title: item.title || "",
          content: item.content || "",
          metadata: typeof item.metadata === "string" ? item.metadata : JSON.stringify(item.metadata || null),
        };
      });
    }
  });

  const sections: CmsSection[] = Array.isArray(data) ? (data as CmsSection[]) : [];

  const getSection = (key: string): CmsSection | undefined =>
    sections.find((s) => s.key === key);

  const getText = (key: string, fallback = "") => {
    const s = getSection(key);
    return s?.content ?? fallback;
  };

  const getMeta = <T = Record<string, unknown>>(key: string, fallback: T): T => {
    const s = getSection(key);
    if (!s?.metadata) return fallback;
    try {
      return JSON.parse(s.metadata) as T;
    } catch {
      return fallback;
    }
  };

  return { sections, isLoading, getSection, getText, getMeta };
}
